import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useContent } from "../lib/content";
import { Phone, ChevronRight, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const { t } = useContent();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) {
      setErr("Hiányzó visszaállítási token. Kérem nyissa meg újra a kapott e-mailben lévő linket.");
      return;
    }
    if (pw.length < 7) {
      setErr("A jelszónak legalább 7 karakter hosszúnak kell lennie.");
      return;
    }
    if (pw !== pw2) {
      setErr("A két jelszó nem egyezik.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top utility bar */}
      <div className="topbar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone size={11} />
            <span>{t("topbar_phone_label", "HÍVJON")}: {t("topbar_phone", "+36 20 9 40 40 04")}</span>
          </div>
          <nav className="flex items-center gap-1 text-[11px] uppercase">
            <Link to="/" className="px-2 py-1 hover:text-[color:var(--brand)]">Kezdőlap</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/login" className="px-2 py-1 hover:text-[color:var(--brand)]">Belépés</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <span className="px-2 py-1 font-bold" style={{ color: "var(--brand)" }}>Új jelszó</span>
          </nav>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <div
              className="w-14 h-14 flex items-center justify-center rounded shrink-0"
              style={{ background: "var(--text)", color: "#fff", fontWeight: 900, fontSize: 20, letterSpacing: "-0.02em" }}
            >
              {t("header_logo_initials", "P24")}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-xl" style={{ color: "var(--text)" }}>{t("header_company_name", "prnds24.hu")}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{t("header_company_subtitle", "OEM Alkatrészek · Nagyker")}</div>
            </div>
          </Link>
        </div>
      </div>

      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/">Kezdőlap</Link> / <Link to="/login">Bejelentkezés</Link> / <span style={{ color: "var(--text)" }}>Új jelszó beállítása</span>
        </div>
      </div>

      <main className="flex-1 bg-[color:var(--surface-elev,#f5f5f5)]">
        <div className="max-w-[700px] w-full mx-auto px-4 py-10">
          <h1 className="text-2xl font-bold mb-3 uppercase tracking-wide" style={{ color: "var(--text)" }}>
            Új jelszó beállítása
          </h1>
          <div className="border-b-2 mb-6" style={{ borderColor: "var(--brand)" }} />

          <div className="card-elev">
            <div className="section-title">Új jelszó megadása</div>
            <div className="p-6">
              {done ? (
                <div
                  className="text-sm py-3 px-4 flex items-start gap-3"
                  style={{ background: "#EFFAE9", color: "#2C6E1F", border: "1px solid #C9E5B7", borderRadius: 4 }}
                  data-testid="reset-success"
                >
                  <ShieldCheck size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>A jelszavát sikeresen frissítettük.</strong>
                    <div className="mt-1">Átirányítjuk a bejelentkezési oldalra…</div>
                  </div>
                </div>
              ) : !token ? (
                <div
                  className="text-sm py-3 px-4"
                  style={{ background: "#FEE", color: "var(--out-stock)", border: "1px solid #FCC", borderRadius: 4 }}
                  data-testid="reset-token-missing"
                >
                  <strong>Hiányzó token.</strong>{" "}
                  Kérem nyissa meg a kapott e-mailben lévő pontos linket, vagy{" "}
                  <Link to="/forgot-password" className="underline" style={{ color: "var(--brand)" }}>
                    igényeljen újat
                  </Link>.
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                  <p className="text-sm" style={{ color: "var(--text-mute)" }}>
                    Adjon meg egy új jelszót. Minimum <strong>7 karakter</strong> szükséges.
                  </p>

                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:items-center">
                    <label className="text-sm font-bold">
                      Új jelszó: <span style={{ color: "var(--out-stock)" }}>*</span>
                    </label>
                    <input
                      type="password"
                      className="input"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      required
                      autoFocus
                      autoComplete="new-password"
                      data-testid="reset-password-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:items-center">
                    <label className="text-sm font-bold">
                      Új jelszó megerősítése: <span style={{ color: "var(--out-stock)" }}>*</span>
                    </label>
                    <input
                      type="password"
                      className="input"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      required
                      autoComplete="new-password"
                      data-testid="reset-password-confirm-input"
                    />
                  </div>

                  {err && (
                    <div
                      className="text-sm py-2 px-3"
                      style={{ background: "#FEE", color: "var(--out-stock)", border: "1px solid #FCC", borderRadius: 4 }}
                      data-testid="reset-error"
                    >
                      <strong>HIBA:</strong> {err}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-center pt-2">
                    <span />
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary"
                        data-testid="reset-submit-button"
                      >
                        {loading ? "Egy pillanat..." : "Új jelszó mentése"}
                      </button>
                      <Link
                        to="/login"
                        className="text-xs hover:underline"
                        style={{ color: "var(--brand)" }}
                      >
                        ← Vissza a bejelentkezéshez
                      </Link>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="mt-10 border-t" style={{ background: "var(--surface-elev)", borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 text-xs flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--text-mute)" }}>
          <div>{t("footer_text", "prnds24.hu — OEM Alkatrész Nagykereskedés")} · © {new Date().getFullYear()}</div>
          <div>{t("footer_links", "Bejelentkezés · Bevásárlókosár · Kapcsolat")}</div>
        </div>
      </footer>
    </div>
  );
}
